import { create, show, useModal } from "@ebay/nice-modal-react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";

import Button from "ds/components/Button";
import FormField from "ds/components/Form/Field";
import Input from "ds/components/Input";
import ModalCancelButton from "ds/components/Modal/CancelButton";
import ModalContent from "ds/components/Modal/Content";
import ModalFooter from "ds/components/Modal/Footer";
import ModalHeader from "ds/components/Modal/Header";
import ModalSimple from "ds/components/ModalSimple";
import Typography from "ds/components/Typography";
import { createDefaultMutationHook } from "hooks/useMutationHandler";
import useTypedContext from "hooks/useTypedContext";
import useAnalytics from "hooks/useAnalytics";
import {
  GET_USER_GUIDE_CERTIFICATION_LIST,
  USER_GUIDE_CERTIFICATION_CREATE,
} from "shared/UserGuides/gql";
import { CERTIFICATE_GENERATED_EVENT_NAME } from "shared/UserGuides/analytics";
import { CERTIFICATE_DISPLAY_NAME_MAX_LENGTH } from "shared/UserGuides/utils";
import { stringIsRequired } from "utils/formValidators";
import { AccountContext } from "views/AccountWrapper";

const useCreateUserGuideCertification = createDefaultMutationHook(USER_GUIDE_CERTIFICATION_CREATE);

type GenerateCertificateModalProps = {
  groupId: string;
};

type NameFormValues = {
  fullName: string;
};

const GenerateCertificateModal = create(function GenerateCertificateModal({
  groupId,
}: GenerateCertificateModalProps) {
  const modal = useModal();
  const navigate = useNavigate();
  const { viewer } = useTypedContext(AccountContext);
  const [createCertificate, { loading }] = useCreateUserGuideCertification({
    refetchQueries: [GET_USER_GUIDE_CERTIFICATION_LIST],
  });

  const trackSegmentEvent = useAnalytics();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<NameFormValues>({ defaultValues: { fullName: viewer.name } });

  const onSubmit = (values: NameFormValues) => {
    createCertificate(
      {
        groupID: groupId,
        displayName: values.fullName.trim(),
      },
      (data) => {
        if (data?.userGuideCertificationCreate) {
          trackSegmentEvent(CERTIFICATE_GENERATED_EVENT_NAME, { mission: groupId });
          modal.hide();
          navigate(`/mission-control?certificate=${data.userGuideCertificationCreate.id}`);
        }
      }
    );
  };

  return (
    <ModalSimple isDismissable={!loading}>
      <ModalHeader title="Add your full name" />
      <ModalContent>
        <Typography variant="p-body3" tag="p">
          Add your full name and we will generate the certificate.
        </Typography>
        <FormField label="Full name" error={errors.fullName?.message}>
          {({ ariaInputProps }) => (
            <Input
              placeholder="Your full name"
              error={!!errors.fullName}
              autoFocus
              maxLength={CERTIFICATE_DISPLAY_NAME_MAX_LENGTH}
              {...register("fullName", {
                validate: stringIsRequired("Full name is required."),
              })}
              {...ariaInputProps}
            />
          )}
        </FormField>
      </ModalContent>
      <ModalFooter>
        <ModalCancelButton onClick={modal.hide} />
        <Button variant="primary" loading={loading} onPress={() => handleSubmit(onSubmit)()}>
          Generate
        </Button>
      </ModalFooter>
    </ModalSimple>
  );
});

export const showGenerateCertificateModal = (props: GenerateCertificateModalProps) =>
  show(GenerateCertificateModal, props);
